import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import { GS_DECISION } from './constant';
import { GSAssessmentBox } from './GSAssessmentBox';
import { PAGE_TEXT } from '../../../lang';
import {
  useSubmitGsDecision,
  useGetDsrcDeclaration,
  type GsDecision,
  type SupersedeData,
} from '../../../graphql';
import {
  Loading,
  Alert,
  type AlertDetails,
  Accordion,
  Dialog,
  type DialogDetails,
  UserRole,
} from '../../../../../components';
import { type ApplicationRemarkType } from '../../../../Nomination/graphql';

type GsAssessmentProps = {
  isViewMode?: boolean;
  initialValues: GsDecision;
  previewData: SupersedeData;
  remarks: ApplicationRemarkType[];
  refetchGsDecision?: () => void;
  title: string;
};

export const GSAssessmentContent: React.FunctionComponent<
  GsAssessmentProps
> = props => {
  const { applicationId = '' } = useParams();
  const {
    isViewMode = false,
    title,
    initialValues,
    previewData: initialPreviewData,
    remarks: returnRemarks,
    refetchGsDecision = () => {},
  } = props;

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogDetails, setDialogDetails] = useState<DialogDetails>({
    title: '',
    content: '',
  });

  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });
  const {
    dsrcDeclaration,
    gsAssigneeOption,
    loading,
    error: dsrcDeclarationError,
  } = useGetDsrcDeclaration(applicationId);

  const { remarks: remarksFromDsrc } = dsrcDeclaration;

  if (dsrcDeclarationError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` + (dsrcDeclarationError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const handleBackTolist = (): void => {
    window.location.href = '/internal/gs-homepage/gsassessments?am=2';
  };

  const handleCloseDialog = (): void => {
    setDialogOpen(false);
  };

  const submitGsDecision = useSubmitGsDecision();
  const handleSubmit = async (value: GsDecision): Promise<void> => {
    setDialogOpen(false);
    const { remarks, decision, gsDecision, fileChecks } = value;

    try {
      const variables = {
        input: {
          applicationId,
          decision: decision?.includes('RETURN') ? gsDecision : decision,

          ...(decision?.includes('RETURN') ? { remarks } : {}),
          ...(decision === GS_DECISION.OFFER_PROVISIONED ? { fileChecks } : {}),
        },
      };

      await submitGsDecision({
        variables,
      });

      if (decision === GS_DECISION.OFFER_PROVISIONED) {
        refetchGsDecision();
        setAlertDetails({
          alertContent: 'Saved GS Decision Successfully.',
          alertType: 'success',
        });
        setOpenAlert(true);
      } else {
        setDialogDetails(dialogMappings[PAGE_TEXT.CLOSE]);
        setDialogOpen(true);
      }
    } catch (error: any) {
      setAlertDetails({
        alertContent: `Saved GS Decision Failed.${(error as Error).message}`,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const dialogMappings: Record<string, DialogDetails> = {
    // on submit successfully
    [PAGE_TEXT.CLOSE]: {
      title: '',
      content:
        PAGE_TEXT.GS_ASSESSMENT_DETAILS.ASSESSMENT.SUBMIT_SUCCESS.CONTENT,
      confirmAction: PAGE_TEXT.BACK,
      handleDialogConfirmAction: handleBackTolist,
    },
    // on open when it is a returned assessment
    [PAGE_TEXT.GS_ASSESSMENT_DETAILS.ASSESSMENT.PREVIEW_DIALOG.TITLE]: {
      title: PAGE_TEXT.GS_ASSESSMENT_DETAILS.ASSESSMENT.PREVIEW_DIALOG.TITLE,
      content:
        PAGE_TEXT.GS_ASSESSMENT_DETAILS.ASSESSMENT.PREVIEW_DIALOG.CONTENT,
      confirmAction: PAGE_TEXT.OK,
      handleDialogConfirmAction: handleCloseDialog,
    },
  };

  useEffect(() => {
    const gsRelatedRemark = returnRemarks.filter(
      rmk => rmk.fromRole === UserRole.GS || rmk.toRole.includes(UserRole.GS),
    );
    if (gsRelatedRemark.length && !loading && !isViewMode) {
      setDialogDetails(
        dialogMappings[
          PAGE_TEXT.GS_ASSESSMENT_DETAILS.ASSESSMENT.PREVIEW_DIALOG.TITLE
        ],
      );
      setDialogOpen(true);
    }
  }, [loading]);

  return (
    <div>
      <ContentBox>
        <Dialog
          openDialog={dialogOpen}
          width={'700px'}
          dialogTitle={dialogDetails.title}
          dialogAction={dialogDetails.confirmAction}
          cancelAction={dialogDetails.cancelAction ?? undefined}
          onDialogAction={dialogDetails.handleDialogConfirmAction}
          onDialogClose={handleCloseDialog}
        >
          <Typography margin={'0rem 1.5rem 2rem'}>
            {dialogDetails.content}
          </Typography>
        </Dialog>
        <Alert
          openAlert={openAlert}
          setOpenAlert={setOpenAlert}
          alertDetails={alertDetails}
        />
        {loading ? (
          <Loading />
        ) : (
          <Accordion
            className="w-full"
            defaultExpanded
            title={`${PAGE_TEXT.GS_ASSESSMENT_DETAILS.ASSESSMENT.TITLE} - ${title}`}
            details={
              <GSAssessmentBox
                remarksFromDsrc={remarksFromDsrc}
                gsAssigneeOption={gsAssigneeOption}
                initialValues={initialValues}
                refetchGsDecision={refetchGsDecision}
                onSubmit={handleSubmit}
                isViewMode={isViewMode}
                previewData={initialPreviewData}
              />
            }
          />
        )}
      </ContentBox>
    </div>
  );
};

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;

  Accordion {
    margin-bottom: 1rem;
  }
`;
